import { useEffect, useState } from "react";
import api, { billingErrorMessage } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import { toast } from "sonner";

const PHASES = [
  { key: "thirty_day", label: "30 days" },
  { key: "sixty_day", label: "60 days" },
  { key: "ninety_day", label: "90 days" },
];

const FIXED_SECTIONS = [
  ["keyword_clusters", "Keyword clusters"],
  ["internal_linking", "Internal linking"],
  ["ai_search_optimizations", "AI search optimization"],
  ["conversion_funnel", "Conversion funnel"],
  ["authority_outreach", "Authority outreach"],
];

export default function GrowthPlanPage() {
  const { activeProjectId, activeProject } = useProjects();
  const [plan, setPlan] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePhase, setActivePhase] = useState("thirty_day");

  useEffect(() => {
    if (!activeProjectId) return;
    api.get(`/growth-plan/${activeProjectId}/latest`).then(({ data }) => {
      if (data?.plan) setPlan(data.plan);
    });
    api.get(`/meta/industry-vocab/${activeProjectId}`).then(({ data }) => setVocab(data));
  }, [activeProjectId]);

  const generate = async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/growth-plan", { project_id: activeProjectId });
      setPlan(data.plan);
      toast.success("Growth plan generated.");
    } catch (e) {
      toast.error(billingErrorMessage(e) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  // Adaptive section list: industry-specific page types first, then fixed sections.
  const industrySections = (vocab?.page_types || []).map((p) => [p.key, p.label]);
  const sections = [
    industrySections[0] ? ["keyword_clusters", "Keyword clusters"] : ["keyword_clusters", "Keyword clusters"],
    ...industrySections,
    ...FIXED_SECTIONS.slice(1),
  ];

  return (
    <DashboardLayout>
      <PageHeader
        overline="Growth Plan"
        title="A 30 / 60 / 90-day plan, calibrated to revenue."
        description={
          activeProject
            ? `Adapted to ${activeProject.business_model}${activeProject.industry ? ` · ${activeProject.industry}` : ""}. Click Generate to refresh — then assign sections to the content calendar.`
            : "Click Generate to refresh — then assign sections to the content calendar."
        }
        actions={
          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-plan-btn"
            className="px-5 py-2.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50"
          >
            {loading ? "Generating…" : "Generate Growth Plan"}
          </button>
        }
      />

      {!plan && (
        <div className="bg-white border border-dashed border-border rounded-sm p-12 text-center" data-testid="growth-plan-empty">
          <p className="font-serif text-xl text-muted-foreground">No plan yet. Generate your first 30/60/90-day plan.</p>
        </div>
      )}

      {plan && (
        <>
          <div className="bg-white border border-border rounded-sm p-6 mb-6">
            <p className="editorial-overline">Executive summary</p>
            <p className="font-serif text-xl mt-2 leading-relaxed">{plan.summary}</p>
          </div>

          <div className="flex gap-2 mb-5">
            {PHASES.map((p) => (
              <button
                key={p.key}
                onClick={() => setActivePhase(p.key)}
                data-testid={`phase-${p.key}`}
                className={`px-5 py-2.5 text-sm rounded-sm border transition-colors ${
                  activePhase === p.key
                    ? "bg-navy-900 text-ivory-50 border-navy-900"
                    : "bg-white text-navy-900 border-border hover:border-gold-500"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="bg-white border border-border rounded-sm p-6" data-testid="growth-plan-phase">
            <p className="editorial-overline">{plan[activePhase]?.estimated_impact}</p>
            <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sections.map(([k, label]) => {
                const items = plan[activePhase]?.[k] || [];
                if (!items.length) return null;
                return (
                  <div key={k} className="border-l-2 border-gold-500 pl-4" data-testid={`plan-section-${k}`}>
                    <p className="font-serif text-lg">{label}</p>
                    <ul className="mt-2 space-y-1.5">
                      {items.map((it) => (
                        <li key={it} className="text-sm text-navy-900/80 leading-relaxed">• {it}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
